import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { IconBook, IconCreditCard, IconKey, IconSparkles } from '@tabler/icons-react';
import { List, Title } from '@mantine/core';
import { Footer } from '../../components/Footer';
import ScrollToTop from '../../components/ScrollToTop';

function Privacy() {
  useEffect(() => {
    document.title = 'Privacy | PromptSlate';
  }, []);

  return (
    <div className="flex flex-col w-full items-center h-fit">
      <ScrollToTop />
      <div className="w-full h-[45px] flex justify-center border-b">
        <div className="w-full max-w-5xl h-full flex items-center justify-between text-sm">
          <div>Introducting PromptSlate</div>
          <Link to="/signup">
            <div className="flex flex-row">
              Get started now <IconSparkles size="1rem" className="ml-2 mt-0.5" />
            </div>
          </Link>
        </div>
      </div>
      <div className="w-full max-w-5xl mt-2 mb-12">
        <div className="w-full max-w-5xl h-[70px] flex items-center justify-between">
          <div>
            <Link to="/">
              <img
                src="/logo_black.png"
                alt="PromptSlate logo"
                className="h-[30px] object-contain"
              />
            </Link>
          </div>
          <div className="flex flex-row">
            <div className="flex flex-row mr-5 p-2 rounded-lg hover:bg-slate-100 cursor-pointer duration-100 ease-in-out">
              <IconBook size="1.5rem" className="mr-1" /> Docs
            </div>
            <div className="flex flex-row mr-4 p-2 rounded-lg hover:bg-slate-100 cursor-pointer duration-100 ease-in-out">
              <IconCreditCard size="1.5rem" className="mr-1" /> Pricing
            </div>
            <Link to="/login">
              <div className="flex flex-row p-2 rounded-lg hover:bg-slate-100 cursor-pointer duration-100 ease-in-out">
                <IconKey size="1.5rem" className="mr-1" /> Login
              </div>
            </Link>
          </div>
        </div>
        <div className="mt-12">
          <div className="max-w-[50%] text-5xl font-semibold leading-[55px]">Privacy</div>
          <span>
            By continuing to use our platform, you agree that you read the privacy policy.
          </span>
          <div className="mt-8 flex flex-col gap-4 text-lg">
            <Title order={1}>Introduction</Title>
            <p>
              Welcome to PromptSlate ("we", "our", "us"). By accessing or using our website located
              at promptslate.com (the "Website") (hereinafter referred to also as “Service”), you
              agree to the collection and use of information in accordance with this policy. Unless
              otherwise defined in this Privacy Policy, the terms used in this Privacy Policy have
              the same meanings as in our Terms and Conditions. If you do not agree with the Terms
              or Privacy Policy, please do not use our Website.
            </p>
            <p>
              Our Terms and Conditions (“Terms”) govern all use of our Service and, together with
              the Privacy Policy, constitutes your agreement with us (“agreement”).
            </p>
            <Title order={1}>Definitions</Title>
            <p>
              Service means the https://promptslate.com website and platforms operated by
              PromptSlate.
            </p>
            <p>
              Personal Data means data about a living individual who can be identified from those
              data (or from those and other information either in our possession or likely to come
              into our possession).
            </p>
            <p>
              Usage Data is data collected automatically either generated by the use of Service or
              from Service infrastructure itself (for example, the duration of a page visit).
            </p>
            <p>Cookies are small files stored on your device (computer or mobile device).</p>
            <p>
              Data Controller means a natural or legal person who (either alone or jointly or in
              common with other persons) determines the purposes for which and the manner in which
              any personal data are, or are to be, processed. For the purpose of this Privacy
              Policy, we are a Data Controller of your data.
            </p>
            <p>
              Data Processors (or service providers) means any natural or legal person who processes
              the data on behalf of the Data Controller. We may use the services of various Service
              Providers in order to process your data more effectively.
            </p>
            <p>Data Subject is any living individual who is the subject of Personal Data.</p>
            <p>
              The User is the individual using our Service. The User corresponds to the Data
              Subject, who is the subject of Personal Data.
            </p>
            <Title order={1}>Information Collection and Use</Title>
            <p>
              We collect several different types of information for various purposes to provide and
              improve our Service to you.
            </p>
            <Title order={1}>Types of Data Collected</Title>
            <Title order={2}>Personal Data</Title>
            <p>
              While using our Service, we may ask you to provide us with certain personally
              identifiable information that can be used to contact or identify you (“Personal
              Data”). Personally identifiable information may include, but is not limited to:
            </p>
            <List>
              <List.Item>Email address</List.Item>
              <List.Item>First name and last name</List.Item>
              <List.Item>Cookies and Usage Data</List.Item>
            </List>
            <p>
              We may use your Personal Data to contact you with newsletters, marketing or
              promotional materials, and other information that may be of interest to you. You may
              opt out of receiving any, or all, of these communications from us by following the
              unsubscribe link or by emailing at support@promptslate.com.
            </p>
            <Title order={2}>Personal Data</Title>
            <p>
              We may also collect information that your browser sends whenever you visit our Service
              or when you access Service by or through a mobile device (“Usage Data”). This Usage
              Data may include information such as your computer's Internet Protocol address (e.g.
              IP address), browser type, browser version, the pages of our Service that you visit,
              the time and date of your visit, the time spent on those pages, unique device
              identifiers and other diagnostic data.
            </p>
            <p>
              When you access Service with a mobile device, this Usage Data may include information
              such as the type of mobile device you use, your mobile device unique ID, the IP
              address of your mobile device, your mobile operating system, the type of mobile
              Internet browser you use, unique device identifiers and other diagnostic data.
            </p>
            <p>
              We use cookies and similar tracking technologies to track the activity on our Service
              and we hold certain information.
            </p>
            <p>
              Cookies are files with a small amount of data which may include an anonymous unique
              identifier. Cookies are sent to your browser from a website and stored on your device.
              Other tracking technologies are also used such as beacons, tags and scripts to collect
              and track information and to improve and analyze our Service.
            </p>
            <p>
              You can instruct your browser to refuse all cookies or to indicate when a cookie is
              being sent. However, if you do not accept cookies, you may not be able to use some
              portions of our Service.
            </p>
            <p>Examples of Cookies we use:</p>
            <List>
              <List.Item>Session Cookies: We use Session Cookies to operate our Service.</List.Item>
              <List.Item>
                Preference Cookies: We use Preference Cookies to remember your preferences and
                various settings.
              </List.Item>
              <List.Item>
                Security Cookies: We use Security Cookies for security purposes.
              </List.Item>
              <List.Item>
                Advertising Cookies: Advertising Cookies are used to serve you with advertisements
                that may be relevant to you and your interests.
              </List.Item>
            </List>
            <Title order={1}>Use of Data</Title>
            <p>PromptSlate uses the collected data for various purposes:</p>
            <p>Personal Data:</p>
            <List>
              <List.Item>to provide and maintain our Service;</List.Item>
              <List.Item>to notify you about changes to our Service;</List.Item>
              <List.Item>
                to allow you to participate in interactive features of our Service when you choose
                to do so;
              </List.Item>
              <List.Item>to provide customer support;</List.Item>
              <List.Item>
                to gather analysis or valuable information so that we can improve our Service;
              </List.Item>
              <List.Item>to monitor the usage of our Service;</List.Item>
              <List.Item>to detect, prevent and address technical issues;</List.Item>
              <List.Item>to fulfill any other purpose for which you provide it;</List.Item>
              <List.Item>
                to carry out our obligations and enforce our rights arising from any contracts
                entered into between you and us, including for billing and collection;
              </List.Item>
              <List.Item>
                to provide you with notices about your account and/or subscription, including
                expiration and renewal notices, email-instructions, etc.;
              </List.Item>
              <List.Item>
                to provide you with news, special offers and general information about other goods,
                services and events which we offer that are similar to those that you have already
                purchased or enquired about unless you have opted not to receive such information;
              </List.Item>
              <List.Item>
                in any other way we may describe when you provide the information;
              </List.Item>
              <List.Item>for any other purpose with your consent.</List.Item>
            </List>
            <Title order={1}>Retention of Data</Title>
            <p>
              We will retain your Personal Data only for as long as is necessary for the purposes
              set out in this Privacy Policy. We will retain and use your Personal Data to the
              extent necessary to comply with our legal obligations (for example, if we are required
              to retain your data to comply with applicable laws), resolve disputes, and enforce our
              legal agreements and policies.
            </p>
            <p>
              We will also retain Usage Data for internal analysis purposes. Usage Data is generally
              retained for a shorter period, except when this data is used to strengthen the
              security or to improve the functionality of our Service, or we are legally obligated
              to retain this data for longer time periods.
            </p>
            <Title order={1}>Transfer of Data</Title>
            <p>
              Your information, including Personal Data, may be transferred to – and maintained on –
              computers located outside of your state, province, country, or other governmental
              jurisdiction where the data protection laws may differ from those of your
              jurisdiction. If you are located outside United States and choose to provide
              information to us, please note that we may transfer the data, including Personal Data,
              to United States and process it there.
            </p>
            <p>
              Your consent to this Privacy Policy followed by your submission of such information
              represents your agreement to that transfer.
            </p>
            <p>
              PromptSlate will take all the steps reasonably necessary to ensure that your data is
              treated securely and in accordance with this Privacy Policy, and no transfer of your
              Personal Data will take place to an organisation or a country unless there are
              adequate controls in place including the security of your data and other personal
              information.
            </p>
            <Title order={1}>Transfer of Data</Title>
            <p>We may disclose personal information that we collect, or you provide:</p>
            <Title order={2}>Disclosure for Law Enforcement.</Title>
            <p>
              Under certain circumstances, we may be required to disclose your Personal Data if
              required to do so by law or in response to valid requests by public authorities.
              Business Transaction.
            </p>
            <p>
              If we or our subsidiaries are involved in a merger, acquisition, or asset sale, your
              Personal Data may be transferred.
            </p>
            <p>Other cases. We may disclose your information also:</p>
            <List>
              <List.Item>to fulfill the purpose for which you provide it;</List.Item>
              <List.Item>
                for the purpose of including your company’s logo on our website;
              </List.Item>
              <List.Item>for any other purpose with your consent.</List.Item>
            </List>
            <Title order={1}>Security of Data</Title>
            <p>
              The security of your data is important to us, but remember that no method of
              transmission over the Internet or method of electronic storage is 100% secure. While
              we strive to use commercially acceptable means to protect your Personal Data, we
              cannot guarantee its absolute security.
            </p>
            <Title order={1}>
              Your Data Protection Rights Under General Data Protection Regulation (GDPR)
            </Title>
            <p>
              If you are a resident of the European Union (EU) and European Economic Area (EEA), you
              have certain data protection rights, covered by GDPR. - See more at &nbsp;
              <u>
                <a
                  href="https://eur-lex.europa.eu/eli/reg/2016/679/oj"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                >
                  https://eur-lex.europa.eu/eli/reg/2016/679/oj
                </a>
              </u>
            </p>
            <p>
              We aim to take reasonable steps to allow you to correct, amend, delete, or limit the
              use of your Personal Data.
            </p>
            <p>
              If you wish to be informed what Personal Data we hold about you and if you want it to
              be removed from our systems, please email us at support@promptslate.com
            </p>
            <p>In certain circumstances, you have the following data protection rights:</p>
            <List>
              <List.Item>
                the right to access, update, or delete the information we have on you;
              </List.Item>
              <List.Item>
                the right of rectification. You have the right to have your information rectified if
                that information is inaccurate or incomplete;
              </List.Item>
              <List.Item>
                the right to object. You have the right to object to our processing of your Personal
                Data;
              </List.Item>
              <List.Item>
                the right of restriction. You have the right to request that we restrict the
                processing of your personal information;
              </List.Item>
              <List.Item>
                the right to data portability. You have the right to be provided with a copy of your
                Personal Data in a structured, machine-readable, and commonly used format;
              </List.Item>
              <List.Item>
                the right to withdraw consent. You also have the right to withdraw your consent at
                any time where we rely on your consent to process your personal information;
              </List.Item>
            </List>
            <p>
              Please note that we may ask you to verify your identity before responding to such
              requests. Please note that we may not be able to provide Service without some
              necessary data.
            </p>
            <p>
              You have the right to complain to a Data Protection Authority about our collection and
              use of your Personal Data. For more information, please contact your local data
              protection authority in the European Economic Area (EEA).
            </p>
            <Title order={1}>
              Your Data Protection Rights under the California Privacy Protection Act (CalOPPA)
            </Title>
            <p>
              CalOPPA is the first state law in the nation to require commercial websites and online
              services to post a privacy policy. The law's reach stretches well beyond California to
              require a person or company in the United States (and conceivable the world) that
              operates websites collecting personally identifiable information from California
              consumers to post a conspicuous privacy policy on its website stating exactly the
              information being collected and those individuals with whom it is being shared, and to
              comply with this policy. - See more at: &nbsp;
              <u>
                <a
                  href="https://consumercal.org/about-cfc/cfc-education-foundation/california-online-privacy-protection-act-caloppa-3/"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                >
                  https://consumercal.org/about-cfc/cfc-education-foundation/california-online-privacy-protection-act-caloppa-3/
                </a>
              </u>
            </p>
            <p>According to CalOPPA we agree to the following:</p>
            <List>
              <List.Item>users can visit our site anonymously;</List.Item>
              <List.Item>
                our Privacy Policy link includes the word “Privacy”, and can easily be found on the
                page specified above on the home page of our website;
              </List.Item>
              <List.Item>
                users will be notified of any privacy policy changes on our Privacy Policy Page;
              </List.Item>
              <List.Item>
                users are able to change their personal information by emailing us at
                support@promptslate.com
              </List.Item>
            </List>
            <Title order={1}>Our Policy on “Do Not Track” Signals:</Title>
            <p>According to CalOPPA, we agree to the following:</p>
            <p>
              We honor Do Not Track signals and do not track, plant cookies, or use advertising when
              a Do Not Track browser mechanism is in place. Do Not Track is a preference you can set
              in your web browser to inform websites that you do not want to be tracked.
            </p>
            <p>
              You can enable or disable Do Not Track by visiting the Preferences or Settings page of
              your web browser.
            </p>
            <Title order={1}>Service Providers</Title>
            <p>
              We may employ third-party companies and individuals to facilitate our Service
              (“Service Providers”), provide Service on our behalf, perform Service-related
              services, or assist us in analysing how our Service is used.
            </p>
            <p>
              These third parties have access to your Personal Data only to perform these tasks on
              our behalf and are obligated not to disclose or use it for any other purpose.
            </p>
            <Title order={1}>Analytics</Title>
            <p>
              We may use third-party Service Providers to monitor and analyze the use of our
              Service.
            </p>
            <Title order={2}>Google Analytics</Title>
            <p>
              Google Analytics is a web analytics service offered by Google that tracks and reports
              website traffic. Google uses the data collected to track and monitor the use of our
              Service. This data is shared with other Google services. Google may use the collected
              data to contextualise and personalise the ads of its own advertising network
            </p>
            <p>
              For more information on the privacy practices of Google, please visit the Google
              Privacy Terms web page: &nbsp;
              <u>
                <a
                  href="https://policies.google.com/privacy?hl=en"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                >
                  https://policies.google.com/privacy?hl=en
                </a>
              </u>
            </p>
            <Title order={1}>Behavioral Remarketing</Title>
            <p>
              PromptSlate may use remarketing services to advertise on third party websites to you
              after you visited our Service. We and our third-party vendors use cookies to inform,
              optimise and serve ads based on your past visits to our Service.
            </p>
            <Title order={2}>Google Ads (AdWords)</Title>
            <p>Google Ads (AdWords) remarketing service is provided by Google Inc.</p>
            <p>
              You can opt-out of Google Analytics for Display Advertising and customize the Google
              Display Network ads by visiting the Google Ads Settings page: &nbsp;
              <u>
                <a
                  href="http://www.google.com/settings/ads"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                >
                  http://www.google.com/settings/ads
                </a>
              </u>
            </p>
            <p>
              Google also recommends installing the Google Analytics Opt-out Browser Add-on - &nbsp;
              <u>
                <a
                  href="https://tools.google.com/dlpage/gaoptout"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                >
                  https://tools.google.com/dlpage/gaoptout
                </a>
              </u>
              &nbsp; - for your web browser. Google Analytics Opt-out Browser Add-on provides
              visitors with the ability to prevent their data from being collected and used by
              Google Analytics. For more information on the privacy practices of Google, please
              visit the Google Privacy Terms web page: &nbsp;
              <u>
                <a
                  href="https://policies.google.com/privacy?hl=en"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                >
                  https://policies.google.com/privacy?hl=en
                </a>
              </u>
            </p>
            <p>
              For more information on the privacy practices of Google, please visit the Google
              Privacy Terms web page: &nbsp;
              <u>
                <a
                  href="http://www.google.com/settings/ads"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                >
                  http://www.google.com/settings/ads
                </a>
              </u>
            </p>
            <Title order={2}>Facebook</Title>
            <p>Facebook remarketing service is provided by Facebook Inc.</p>
            <p>
              Facebook remarketing service is provided by Facebook Inc. You can learn more about
              interest-based advertising from Facebook by visiting this page: &nbsp;
              <u>
                <a
                  href="https://www.facebook.com/help/164968693837950"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                >
                  https://www.facebook.com/help/164968693837950
                </a>
              </u>
            </p>
            <p>
              To opt-out from Facebook's interest-based ads, follow these instructions from
              Facebook:&nbsp;
              <u>
                <a
                  href="https://www.facebook.com/help/568137493302217"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                >
                  https://www.facebook.com/help/568137493302217
                </a>
              </u>{' '}
            </p>
            <p>
              Facebook adheres to the Self-Regulatory Principles for Online Behavioural Advertising
              established by the Digital Advertising Alliance. You can also opt-out from Facebook
              and other participating companies through the Digital Advertising Alliance in the USA
              &nbsp;
              <u>
                <a
                  href="http://www.aboutads.info/choices/"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                >
                  http://www.aboutads.info/choices/
                </a>
              </u>
              , the Digital Advertising Alliance of Canada in Canada &nbsp;
              <u>
                <a
                  href="http://youradchoices.ca/"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                >
                  http://youradchoices.ca/
                </a>
              </u>{' '}
              or the European Interactive Digital Advertising Alliance in Europe&nbsp;
              <u>
                <a
                  href="http://www.youronlinechoices.eu/"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                >
                  http://www.youronlinechoices.eu/
                </a>
              </u>{' '}
              , or opt-out using your mobile device settings.
            </p>
            <p>
              For more information on the privacy practices of Facebook, please visit Facebook's
              Data Policy: &nbsp;
              <u>
                <a
                  href="https://www.facebook.com/privacy/explanation/"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                >
                  https://www.facebook.com/privacy/explanation/
                </a>
              </u>
            </p>
            <Title order={1}>Payments</Title>
            <p>
              We may provide paid products and/or services within Service. In that case, we use
              third-party services for payment processing (e.g. payment processors).
            </p>
            <p>
              We will not store or collect your payment card details. That information is provided
              directly to our third-party payment processors whose use of your personal information
              is governed by their Privacy Policy. These payment processors adhere to the standards
              set by PCI-DSS as managed by the PCI Security Standards Council, which is a joint
              effort of brands like Visa, Mastercard, American Express and Discover. PCI-DSS
              requirements help ensure the secure handling of payment information.
            </p>
            <p>The payment processors we work with are:</p>
            <Title order={2}>Paddle</Title>
            <p>
              Paddle is our merchant of record. Their privacy policy can be viewed at &nbsp;
              <u>
                <a
                  href="https://www.paddle.com/legal/privacy"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                >
                  https://www.paddle.com/legal/privacy
                </a>
              </u>
            </p>
            <Title order={1}>Links to Other Sites</Title>
            <p>
              Our Service may contain links to other sites that are not operated by us. If you click
              a third party link, you will be directed to that third party's site. We strongly
              advise you to review the Privacy Policy of every site you visit.
            </p>
            <p>
              We have no control over and assume no responsibility for the content, privacy policies
              or practices of any third party sites or services.
            </p>
            <Title order={1}>Children's Privacy</Title>
            <p>
              Our Services are not intended for use by children under the age of 18 (“Child” or
              “Children”).
            </p>
            <p>
              We do not knowingly collect personally identifiable information from Children under
              18. If you become aware that a Child has provided us with Personal Data, please
              contact us by emailing at <u>support@promptslate.com</u>. If we become aware that we
              have collected Personal Data from Children without verification of parental consent,
              we take steps to remove that information from our servers.
            </p>
            <Title order={1}>Changes to This Privacy Policy</Title>
            <p>
              We may update our Privacy Policy from time to time. We will notify you of any changes
              by posting the new Privacy Policy on this page.
            </p>
            <p>
              We will let you know via email and/or a prominent notice on our Service, prior to the
              change becoming effective, and update “effective date” at the top of this Privacy
              Policy.
            </p>
            <p>
              You are advised to review this Privacy Policy periodically for any changes. Changes to
              this Privacy Policy are effective when they are posted on this page.
            </p>
            <Title order={1}>Contact Us</Title>
            <p>
              If you have any questions about this Privacy Policy, please contact us by email:
              support@promptslate.com
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default Privacy;
